import { CheckCircle } from 'lucide-react';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Button } from '~/shared/components/buttons/Button';
import { TableActions } from '~/shared/data-tables/TableActions';
import { EvvExceptionSeverityEnum, EvvExceptionStatusEnum, getScheduleDashboardOptions, resolveEvvExceptionOptions } from '@generated';
import { ReviewEvvExceptionFormModal } from './ReviewEvvExceptionFormModal';
import { DisciplineChip } from '~/libraries/disciplines/DisciplineChip';
import { Badge } from '~/shared/components/Badge';

const EvvExceptionsDataTable = createQueryDataTable('evv-exception');
type RowType = RowOfQueryDataTable<typeof EvvExceptionsDataTable>;
type TableProps = PropsOfQueryDataTable<typeof EvvExceptionsDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const resolveEvvException = useMutation({
		...resolveEvvExceptionOptions,
		invalidateQueryKeys: [getScheduleDashboardOptions().queryKey, getSearchQueryKey('evv-exception')]
	});

	const handleResolve = () => {
		resolveEvvException.mutate({ evvExceptionId: row.id, dispute: false, notes: null });
	};

	return (
		<TableActions>
			{row.exceptionStatusId !== EvvExceptionStatusEnum.Resolved && (
				<Button
						Icon={CheckCircle}
						aria-label="Resolve EVV Exception"
						intent="primary"
						appearance="ghost"
						onClick={handleResolve}
						isPending={resolveEvvException.isPending}
					/>
			)}
			<TableActions.ViewButton
				aria-label="View EVV Exception"
				renderModal={closeModal => <ReviewEvvExceptionFormModal exception={row} closeModal={closeModal} />}
			/>
		</TableActions>
	);
}

export function StatusBadge(props: { exceptionStatusId: number; exceptionStatusLabel: string }) {
	return (
		<Badge
			intent={
				props.exceptionStatusId === EvvExceptionStatusEnum.Resolved
					? 'success'
					: props.exceptionStatusId === EvvExceptionStatusEnum.Pending
					? 'warning'
					: 'neutral'
			}
		>
			{props.exceptionStatusLabel}
		</Badge>
	);
}

export function SeverityBadge(props: { exceptionSeverityId: number; exceptionSeverityLabel: string }) {
	return (
		<Badge
			intent={
				props.exceptionSeverityId === EvvExceptionSeverityEnum.Critical
					? 'destructive'
					: props.exceptionSeverityId === EvvExceptionSeverityEnum.Warning
					? 'warning'
					: 'neutral'
			}
		>
			{props.exceptionSeverityLabel}
		</Badge>
	);
}

const columnDefinitions = EvvExceptionsDataTable.columns([
	{
		header: 'Date',
		key: 'occurredOn',
		sortable: true,
		renderCell: row => row.occurredOn.toLocaleDateString()
	},
	{
		header: 'Paient',
		key: 'patientName',
		renderCell: row => (
			<span className="font-semibold">
				{row.patientLastName} (#{row.patientIdentifier})
			</span>
		)
	},
	{
		header: 'Clinician',
		key: 'performingUserName'
	},
	{
		header: 'Discipline',
		key: 'visitDisciplineId',
		renderCell: row => <DisciplineChip disciplineId={row.visitDisciplineId} />
	},
	{
		header: 'Exception',
		key: 'exceptionTypeLabel'
	},
	{
		header: 'Severity',
		key: 'exceptionTypeLabel',
		renderCell: row => <SeverityBadge {...row} />
	},
	{
		header: 'Detail',
		key: 'detail',
		renderCell: row => (
			<span className="block max-w-[300px] truncate" title={row.detail}>
				{row.detail}
			</span>
		)
	},
	{
		header: 'Status',
		key: 'exceptionStatusLabel',
		renderCell: row => <StatusBadge {...row} />
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function EvvExceptionTable(props: TableProps) {
	return <EvvExceptionsDataTable.Table columns={columnDefinitions} emptyMessage="No exceptions found." {...props} />;
}
